import React, { useState, useEffect } from 'react';
import { calculateScenarios, isDevMode, useMockData, setMockDataPreference } from '../../utils/api';
import { CalculatorForm } from './CalculatorForm';
import { DevModeToggle } from './DevModeToggle';
import type { CalculationResults } from '../../types';
import type { CalculatorInputs } from './types';
import { logger } from '../../utils/logging';

interface CalculatorProps {
  onCalculate: (results: CalculationResults | null, isLoading: boolean) => void;
}

const STORAGE_KEY = 'hoaCalculatorInputs';
const MIN_LOADING_TIME = 6000; // 6 seconds minimum loading time

const defaultInputs: CalculatorInputs = {
  currentReserveFund: 1000000,
  annualDuesAndFees: 100000,
  annualExpenses: 50000,
  recentInvestmentReturns: 5, // This is stored as a percentage (e.g., 5 means 5%)
  timeRangeYears: 5
};

export function Calculator({ onCalculate }: CalculatorProps) {
  const [inputs, setInputs] = useState<CalculatorInputs>(() => {
    const savedInputs = localStorage.getItem(STORAGE_KEY);
    return savedInputs ? JSON.parse(savedInputs) : defaultInputs;
  });

  const [isCalculating, setIsCalculating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [isMockData, setIsMockData] = useState(useMockData());
  const [calculationResults, setCalculationResults] = useState<CalculationResults | null>(null);
  const showDevTools = isDevMode();

  useEffect(() => {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(inputs));
  }, [inputs]);

  const handleToggleMockData = () => {
    const newValue = !isMockData;
    logger.info('Mock data toggle clicked', { 
      currentValue: isMockData,
      newValue
    });
    setIsMockData(newValue);
    setMockDataPreference(newValue);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    setError(null);
    setIsCalculating(true);
    onCalculate(null, true);
    
    const startTime = Date.now();
    
    try {
      const investmentReturnsDecimal = inputs.recentInvestmentReturns / 100;
      
      logger.info('Starting calculation', { 
        ...inputs,
        recentInvestmentReturnsPercent: inputs.recentInvestmentReturns,
        recentInvestmentReturnsDecimal: investmentReturnsDecimal,
        usingMockData: isMockData 
      });

      const results = await calculateScenarios(
        inputs.currentReserveFund,
        inputs.annualDuesAndFees,
        inputs.annualExpenses,
        investmentReturnsDecimal,
        inputs.timeRangeYears
      );

      setCalculationResults(results);

      logger.info('Calculation completed successfully', {
        hasResults: true,
        dataPoints: results.dates.length
      });

      // Calculate remaining time to meet minimum loading time
      const elapsedTime = Date.now() - startTime;
      const remainingTime = Math.max(0, MIN_LOADING_TIME - elapsedTime);

      // Wait for the remaining time before showing results
      await new Promise(resolve => setTimeout(resolve, remainingTime));

      onCalculate(results, false);
    } catch (err) {
      const errorMessage = err instanceof Error ? err.message : 'An error occurred while calculating scenarios';
      logger.error('Calculation failed', err instanceof Error ? err : new Error(errorMessage));
      setError(errorMessage);
      onCalculate(null, false);
    } finally {
      setIsCalculating(false);
    }
  };

  return (
    <div className="bg-white rounded-xl shadow-lg overflow-hidden">
      {showDevTools && (
        <DevModeToggle
          mockData={isMockData}
          onToggle={handleToggleMockData}
        />
      )}

      <CalculatorForm
        inputs={inputs}
        setInputs={setInputs}
        error={error}
        isCalculating={isCalculating}
        onSubmit={handleSubmit}
      />
    </div>
  );
}